import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Tag, Share2, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { blogPosts } from "../data/blogData";
import { SEOHead } from "./SEOHead";
import ReactMarkdown from 'react-markdown';

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Article Not Found</h2>
            <p className="text-slate-600 mb-6">The blog post you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button>Back to Blog</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const relatedPosts = blogPosts.filter(p => p.id !== post.id).slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title={`${post.title} | Ovawell Autos Blog`}
        description={post.excerpt}
        keywords={post.tags.join(", ")}
      />

      {/* Hero Image */}
      <div className="relative h-96 bg-slate-900">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 -mt-32 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <Button 
            variant="ghost" 
            className="mb-6 text-white hover:text-white hover:bg-white/10"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Article Header */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <Badge className="mb-4">{post.category}</Badge>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </div>
                <div className="flex items-center gap-2">
                  By {post.author}
                </div>
              </div>

              <p className="text-xl text-slate-600 leading-relaxed">
                {post.excerpt}
              </p>

              <div className="flex items-center gap-4 mt-6 pt-6 border-t">
                <div className="flex flex-wrap gap-2 flex-1">
                  {post.tags.map((tag, index) => (
                    <div key={index} className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                      <Tag className="w-3 h-3" />
                      {tag}
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Article Content */}
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8 prose prose-slate max-w-none">
                  <ReactMarkdown
                    components={{
                      h2: ({node, ...props}) => <h2 className="text-3xl font-bold text-slate-900 mt-8 mb-4" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-2xl font-bold text-slate-900 mt-6 mb-3" {...props} />,
                      p: ({node, ...props}) => <p className="text-slate-700 leading-relaxed mb-4" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-slate-900" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                      li: ({node, ...props}) => <li className="text-slate-700" {...props} />,
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>
                </CardContent>
              </Card>

              {/* Call to Action */}
              <Card className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                <CardContent className="p-8 text-center">
                  <Phone className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <h3 className="text-2xl font-bold mb-2">Need Car Recovery in Hackney?</h3>
                  <p className="text-slate-300 mb-6">
                    24/7 emergency service across East London. 30-minute response time.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="tel:07492639223">
                      <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 w-full sm:w-auto">
                        Call 07492 639223
                      </Button>
                    </a>
                    <Link to="/#quote">
                      <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 w-full sm:w-auto">
                        Get a Quote
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Quick Contact</h3>
                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">24/7 Emergency</p>
                      <a href="tel:07492639223" className="text-lg font-bold text-slate-900 hover:text-slate-700">
                        07492 639223
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Email</p>
                      <a href="mailto:ovawellautoslimited@outlook.com" className="text-sm text-slate-700 hover:text-slate-900">
                        ovawellautoslimited@outlook.com
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Service Area</p>
                      <p className="text-sm text-slate-700">Hackney & 20-Mile Radius</p>
                    </div>
                  </div>
                  <Link to="/#quote">
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">
                      Request Quote
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {relatedPosts.length > 0 && (
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-4">Related Articles</h3>
                    <div className="space-y-4">
                      {relatedPosts.map((relatedPost) => (
                        <Link 
                          key={relatedPost.id}
                          to={`/blog/${relatedPost.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-3">
                            <img 
                              src={relatedPost.image}
                              alt={relatedPost.title}
                              className="w-20 h-20 object-cover rounded"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-slate-900 group-hover:text-slate-700 transition-colors line-clamp-2 mb-1">
                                {relatedPost.title}
                              </h4>
                              <p className="text-xs text-slate-500">{relatedPost.readTime}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <section className="bg-slate-900 text-white py-16 mt-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Read More Articles</h2>
          <p className="text-xl text-slate-300 mb-8">
            Explore more guides and tips about car recovery and transport in East London
          </p>
          <Link to="/blog">
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
              View All Articles
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
